import { Component, computed, forwardRef, input, signal } from '@angular/core';
import {
  ControlValueAccessor,
  FormsModule,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';

@Component({
  selector: 'app-select-filter',
  imports: [
    MatFormFieldModule,
    MatSelectModule,
    FormsModule,
    MatInputModule,
    MatIconModule,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SelectFilterComponent),
      multi: true,
    },
  ],
  templateUrl: './select-filter.component.html',
  styleUrl: './select-filter.component.scss',
})
export class SelectFilterComponent implements ControlValueAccessor {
  placeholder = input('');
  label = input('');
  options = input.required<string[]>();
  optionGroup = input<string[] | undefined>(undefined);
  optionGroupTitle = input<string | undefined>(undefined);

  disabled = false;
  value: string | null = null;

  searchFilter = signal('');
  filteredOptions = computed(() => {
    const filter = this.searchFilter().toLowerCase();
    if (!filter) return this.options();
    return this.options().filter((option) =>
      option.toLowerCase().includes(filter)
    );
  });

  // ControlValueAccessor callbacks
  onChange: (value: string | null) => void = () => {};
  onTouched: () => void = () => {};

  // Keys the embedded input must not leak to mat-select's typeahead
  // (e.g. space would select the highlighted option and close the panel),
  // while still letting navigation and close keys through.
  onSearchKeydown(event: KeyboardEvent) {
    const passthrough = ['ArrowDown', 'ArrowUp', 'Enter', 'Escape', 'Tab'];
    if (!passthrough.includes(event.key)) {
      event.stopPropagation();
    }
  }

  onSelectionChange(value: string) {
    this.value = value;
    this.onChange(value);
    this.onTouched();
    this.searchFilter.set('');
  }

  onOpenedChange(opened: boolean) {
    this.searchFilter.set('');
    if (!opened) {
      this.onTouched();
    }
  }

  // ControlValueAccessor implementation
  writeValue(value: string | null): void {
    this.value = value;
  }

  registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}
